import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import React, { useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { ComponentsManager, PropTypes as PropTypes$1, FieldsManager, ScreensManager, ColorsParser, Tracking } from '@micromag/core';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import uniqBy from 'lodash/uniqBy';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { getJSON } from '@folklore/fetch';
import isObject from 'lodash/isObject';
import { loadGoogleApi, loadGoogleMaps } from '@folklore/services';
import { getDisplayName, getScreenExtraField } from '@micromag/core/utils';
import { generatePath, useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { TrackingContext, TrackingContainer } from '@folklore/tracking';
export { TrackingContext } from '@folklore/tracking';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { v1 } from 'uuid';
import EventEmitter from 'wolfy87-eventemitter';
import { isString as isString$1 } from 'lodash';

var MODALS_NAMESPACE = 'modals';
var FIELDS_NAMESPACE = 'fields';
var FORMS_NAMESPACE = 'forms';
var SCREENS_NAMESPACE = 'screens';
var ELEMENTS_NAMESPACE = 'elements';
var ComponentsContext = /*#__PURE__*/React.createContext(null);

/**
 * Hooks
 */
var useComponentsManager = function useComponentsManager() {
  var namespace = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var manager = useContext(ComponentsContext);
  var finalManager = useMemo(function () {
    return namespace !== null ? new ComponentsManager(manager.getComponents(namespace)) : manager;
  }, [manager, namespace]);
  return finalManager;
};
var useComponents = function useComponents() {
  var namespace = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var defaultComponents = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var manager = useComponentsManager();
  return manager.getComponents(namespace) || defaultComponents;
};
var useComponent = function useComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var namespace = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var manager = useComponentsManager(namespace);
  return useMemo(function () {
    if (!isString(name)) {
      return name || defaultComponent;
    }
    return manager.getComponent(name) || defaultComponent;
  }, [manager, name, defaultComponent]);
};

/**
 * Fields hooks
 */
var useFieldsComponentsManager = function useFieldsComponentsManager() {
  return useComponentsManager(FIELDS_NAMESPACE);
};
var useFieldsComponents = function useFieldsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(FIELDS_NAMESPACE, defaultComponents);
};
var useFieldComponent = function useFieldComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, FIELDS_NAMESPACE);
};

/**
 * Screens hooks
 */
var useScreensComponentsManager = function useScreensComponentsManager() {
  return useComponentsManager(SCREENS_NAMESPACE);
};
var useScreensComponents = function useScreensComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(SCREENS_NAMESPACE, defaultComponents);
};
var useScreenComponent = function useScreenComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, SCREENS_NAMESPACE);
};

/**
 * Forms hooks
 */
var useFormsComponentsManager = function useFormsComponentsManager() {
  return useComponentsManager(FORMS_NAMESPACE);
};
var useFormsComponents = function useFormsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(FORMS_NAMESPACE, defaultComponents);
};
var useFormComponent = function useFormComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, FORMS_NAMESPACE);
};

/**
 * Modals hooks
 */
var useModalsComponentsManager = function useModalsComponentsManager() {
  return useComponentsManager(MODALS_NAMESPACE);
};
var useModalsComponents = function useModalsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(MODALS_NAMESPACE, defaultComponents);
};
var useModalComponent = function useModalComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, MODALS_NAMESPACE);
};

/**
 * Elements hooks
 */
var useElementsComponentsManager = function useElementsComponentsManager() {
  return useComponentsManager(ELEMENTS_NAMESPACE);
};
var useElementsComponents = function useElementsComponents() {
  var defaultComponents = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return useComponents(ELEMENTS_NAMESPACE, defaultComponents);
};
var useElementComponent = function useElementComponent(name) {
  var defaultComponent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return useComponent(name, defaultComponent, ELEMENTS_NAMESPACE);
};

/**
 * Provider
 */
var propTypes$k = {
  children: PropTypes.node.isRequired,
  namespace: PropTypes.string,
  manager: PropTypes.instanceOf(ComponentsManager),
  components: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.func]))
};
var defaultProps$k = {
  namespace: null,
  components: {},
  manager: null
};
var ComponentsProvider = function ComponentsProvider(_ref) {
  var components = _ref.components,
    manager = _ref.manager,
    namespace = _ref.namespace,
    children = _ref.children;
  var previousManager = useComponentsManager() || null;
  var finalManager = useMemo(function () {
    return new ComponentsManager(_objectSpread(_objectSpread(_objectSpread({}, previousManager !== null ? previousManager.getComponents() : null), manager !== null ? manager.getComponents() : null), new ComponentsManager(components).addNamespace(namespace).getComponents()));
  }, [previousManager, manager, components, namespace]);
  return /*#__PURE__*/React.createElement(ComponentsContext.Provider, {
    value: finalManager
  }, children);
};
ComponentsProvider.propTypes = propTypes$k;
ComponentsProvider.defaultProps = defaultProps$k;

var FieldsContext = /*#__PURE__*/React.createContext(null);
var useFieldsManager = function useFieldsManager() {
  return useContext(FieldsContext);
};
var useFieldDefinition = function useFieldDefinition(id) {
  var manager = useFieldsManager();
  return manager.getDefinition(id);
};
var propTypes$j = {
  fields: PropTypes$1.fieldDefinitions,
  manager: PropTypes.instanceOf(FieldsManager),
  children: PropTypes.node.isRequired
};
var defaultProps$j = {
  fields: null,
  manager: null
};
var FieldsProvider = function FieldsProvider(_ref) {
  var fields = _ref.fields,
    manager = _ref.manager,
    children = _ref.children;
  var previousManager = useFieldsManager() || null;
  var finalManager = useMemo(function () {
    var newFields = uniqBy([].concat(_toConsumableArray(fields || []), _toConsumableArray(manager !== null ? manager.getDefinitions() : []), _toConsumableArray(previousManager !== null ? previousManager.getDefinitions() : [])), function (_ref2) {
      var id = _ref2.id;
      return id;
    }).reverse();
    return new FieldsManager(newFields);
  }, [previousManager, manager, fields]);
  var components = useMemo(function () {
    var newComponents = finalManager.getComponents();
    return Object.keys(newComponents).reduce(function (map, id) {
      var component = newComponents[id];
      return isString(component) ? map : _objectSpread(_objectSpread({}, map), {}, _defineProperty({}, id, component));
    }, {});
  }, [finalManager]);
  return /*#__PURE__*/React.createElement(FieldsContext.Provider, {
    value: finalManager
  }, /*#__PURE__*/React.createElement(ComponentsProvider, {
    namespace: FIELDS_NAMESPACE,
    components: components
  }, children));
};
FieldsProvider.propTypes = propTypes$j;
FieldsProvider.defaultProps = defaultProps$j;

var ScreensContext = /*#__PURE__*/React.createContext(new ScreensManager());
var useScreensManager = function useScreensManager() {
  return useContext(ScreensContext);
};
var propTypes$i = {
  screens: PropTypes$1.screenDefinitions,
  namespaces: PropTypes.arrayOf(PropTypes.string),
  filterNamespaces: PropTypes.bool,
  manager: PropTypes.instanceOf(ScreensManager),
  children: PropTypes.node.isRequired
};
var defaultProps$i = {
  screens: null,
  namespaces: null,
  filterNamespaces: false,
  manager: null
};
var ScreensProvider = function ScreensProvider(_ref) {
  var screens = _ref.screens,
    namespaces = _ref.namespaces,
    filterNamespaces = _ref.filterNamespaces,
    manager = _ref.manager,
    children = _ref.children;
  var previousManager = useScreensManager();
  var finalManager = useMemo(function () {
    var newManager = manager !== null ? manager : new ScreensManager(screens);
    if ((previousManager || null) !== null) {
      newManager = previousManager.merge(newManager);
    }
    if (filterNamespaces) {
      newManager = previousManager.filter(function (_ref2) {
        var _ref2$namespaces = _ref2.namespaces,
          screenGroups = _ref2$namespaces === void 0 ? null : _ref2$namespaces;
        return screenGroups === null || namespaces !== null && screenGroups.reduce(function (acc, id) {
          return acc || namespaces.indexOf(id) !== -1;
        }, false);
      });
    }
    return newManager;
  }, [manager, screens, namespaces, filterNamespaces, previousManager]);
  var initialComponents = useMemo(function () {
    return finalManager.getComponents();
  }, [finalManager]);
  var _useState = useState(initialComponents),
    _useState2 = _slicedToArray(_useState, 2),
    components = _useState2[0],
    setComponents = _useState2[1];
  useEffect(function () {
    var onChange = function onChange() {
      return setComponents(finalManager.getComponents());
    };
    finalManager.on('change', onChange);
    return function () {
      finalManager.off('change', onChange);
    };
  }, [finalManager, setComponents]);
  return /*#__PURE__*/React.createElement(ScreensContext.Provider, {
    value: finalManager
  }, /*#__PURE__*/React.createElement(ComponentsProvider, {
    namespace: SCREENS_NAMESPACE,
    components: components
  }, children));
};
ScreensProvider.propTypes = propTypes$i;
ScreensProvider.defaultProps = defaultProps$i;

var StoryContext = /*#__PURE__*/React.createContext(null);
var useStoryContext = function useStoryContext() {
  return useContext(StoryContext);
};
var useStory = function useStory() {
  var story = useStoryContext();
  return story;
};
var propTypes$h = {
  story: PropTypes.oneOfType([PropTypes$1.story, PropTypes$1.theme]),
  children: PropTypes.node.isRequired
};
var defaultProps$h = {
  story: null
};
var StoryProvider = function StoryProvider(_ref) {
  var story = _ref.story,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(StoryContext.Provider, {
    value: story
  }, children);
};
StoryProvider.propTypes = propTypes$h;
StoryProvider.defaultProps = defaultProps$h;

var EditorContext = /*#__PURE__*/React.createContext(null);
var useEditor = function useEditor() {
  return useContext(EditorContext);
};
var useGetColors = function useGetColors() {
  var _ref = useEditor() || {},
    _ref$getColors = _ref.getColors,
    getColors = _ref$getColors === void 0 ? function () {
      return [];
    } : _ref$getColors;
  return getColors;
};
var propTypes$g = {
  children: PropTypes.node.isRequired
};
var defaultProps$g = {};
var EditorProvider = function EditorProvider(_ref2) {
  var children = _ref2.children;
  var story = useStory();
  var screensManager = useScreensManager();
  var fieldsManager = useFieldsManager();
  var parser = useMemo(function () {
    return new ColorsParser({
      screensManager: screensManager,
      fieldsManager: fieldsManager
    });
  }, [screensManager, fieldsManager]);
  var parse = useCallback(function (currentStory) {
    return parser.parse(currentStory);
  }, [parser]);
  var getColors = useCallback(function () {
    return parse(story);
  }, [parse, story]);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  return /*#__PURE__*/React.createElement(EditorContext.Provider, {
    value: {
      getColors: getColors
    }
  }, children);
};
EditorProvider.propTypes = propTypes$g;
EditorProvider.defaultProps = defaultProps$g;

/* eslint-disable react/jsx-props-no-spreading */
var FieldsValueContext = /*#__PURE__*/React.createContext(null);
var useFieldsValue = function useFieldsValue() {
  return useContext(FieldsValueContext);
};
var propTypes$f = {
  value: PropTypes.any,
  // eslint-disable-line react/forbid-prop-types
  children: PropTypes.node.isRequired
};
var defaultProps$f = {
  value: null
};
var FieldsValueContextProvider = function FieldsValueContextProvider(_ref) {
  var value = _ref.value,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(FieldsValueContext.Provider, {
    value: value
  }, children);
};
FieldsValueContextProvider.propTypes = propTypes$f;
FieldsValueContextProvider.defaultProps = defaultProps$f;

/* eslint-disable react/jsx-props-no-spreading */
var FieldContext = /*#__PURE__*/React.createContext(null);
var useFieldContext = function useFieldContext() {
  return useContext(FieldContext);
};
var propTypes$e = {
  context: PropTypes.any,
  // eslint-disable-line react/forbid-prop-types
  children: PropTypes.node.isRequired
};
var defaultProps$e = {
  context: null
};
var FieldContextProvider = function FieldContextProvider(_ref) {
  var context = _ref.context,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(FieldContext.Provider, {
    value: context
  }, children);
};
FieldContextProvider.propTypes = propTypes$e;
FieldContextProvider.defaultProps = defaultProps$e;

/* eslint-disable react/jsx-props-no-spreading */
var GoogleKeysContext = /*#__PURE__*/React.createContext({
  apiKey: null
});
var useGoogleKeys = function useGoogleKeys() {
  return useContext(GoogleKeysContext);
};
var propTypes$d = {
  children: PropTypes.node.isRequired,
  apiKey: PropTypes.string // .isRequired,
};

var defaultProps$d = {
  apiKey: null
};
var GoogleKeysProvider = function GoogleKeysProvider(_ref) {
  var children = _ref.children,
    apiKey = _ref.apiKey;
  var _useGoogleKeys = useGoogleKeys(),
    previousApiKey = _useGoogleKeys.apiKey;
  var value = useMemo(function () {
    return {
      apiKey: apiKey || previousApiKey
    };
  }, [apiKey, previousApiKey]);
  return /*#__PURE__*/React.createElement(GoogleKeysContext.Provider, {
    value: value
  }, children);
};
GoogleKeysProvider.propTypes = propTypes$d;
GoogleKeysProvider.defaultProps = defaultProps$d;

var FontsContext = /*#__PURE__*/React.createContext({
  systemFonts: null,
  googleFonts: null,
  customFonts: null
});
var useGoogleFonts = function useGoogleFonts() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$setFonts = _ref.setFonts,
    setFonts = _ref$setFonts === void 0 ? null : _ref$setFonts;
  var _useGoogleKeys = useGoogleKeys(),
    apiKey = _useGoogleKeys.apiKey;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    googleFonts = _useState2[0],
    setGoogleFonts = _useState2[1];
  useEffect(function () {
    var canceled = false;
    if (apiKey !== null && !disabled) {
      getJSON("https://www.googleapis.com/webfonts/v1/webfonts?key=".concat(apiKey, "&sort=popularity")).then(function (_ref2) {
        var _ref2$items = _ref2.items,
          items = _ref2$items === void 0 ? [] : _ref2$items;
        if (!canceled) {
          var newFonts = items.map(function (it) {
            return {
              type: 'google',
              name: it.family,
              variants: it.variants
            };
          });
          if (setFonts !== null) {
            setFonts(newFonts);
          } else {
            setGoogleFonts(newFonts);
          }
        }
      });
    }
    return function () {
      canceled = true;
    };
  }, [apiKey, disabled, setFonts, setGoogleFonts]);
  return googleFonts;
};
var useFonts = function useFonts() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref3$withoutGoogleFo = _ref3.withoutGoogleFonts,
    withoutGoogleFonts = _ref3$withoutGoogleFo === void 0 ? false : _ref3$withoutGoogleFo;
  var _useContext = useContext(FontsContext),
    _useContext$setGoogle = _useContext.setGoogleFonts,
    setGoogleFonts = _useContext$setGoogle === void 0 ? null : _useContext$setGoogle,
    _useContext$systemFon = _useContext.systemFonts,
    systemFonts = _useContext$systemFon === void 0 ? null : _useContext$systemFon,
    _useContext$googleFon = _useContext.googleFonts,
    googleFonts = _useContext$googleFon === void 0 ? null : _useContext$googleFon,
    _useContext$customFon = _useContext.customFonts,
    customFonts = _useContext$customFon === void 0 ? null : _useContext$customFon;
  useGoogleFonts({
    disabled: withoutGoogleFonts || googleFonts !== null && googleFonts.length > 0,
    setFonts: setGoogleFonts
  });
  var fonts = useMemo(function () {
    return {
      systemFonts: systemFonts,
      googleFonts: googleFonts,
      customFonts: customFonts
    };
  }, [systemFonts, googleFonts, customFonts]);
  return fonts;
};
var propTypes$c = {
  children: PropTypes.node.isRequired,
  systemFonts: PropTypes$1.fonts,
  customFonts: PropTypes$1.fonts
};
var defaultProps$c = {
  systemFonts: ['Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana'],
  customFonts: null
};
var FontsProvider = function FontsProvider(_ref4) {
  var systemFonts = _ref4.systemFonts,
    customFonts = _ref4.customFonts,
    children = _ref4.children;
  var _useFonts = useFonts(),
    _useFonts$systemFonts = _useFonts.systemFonts,
    previousSystemFonts = _useFonts$systemFonts === void 0 ? null : _useFonts$systemFonts,
    _useFonts$googleFonts = _useFonts.googleFonts,
    previousGoogleFonts = _useFonts$googleFonts === void 0 ? null : _useFonts$googleFonts,
    previousCustomFonts = _useFonts.customFonts;
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    googleFonts = _useState4[0],
    setGoogleFonts = _useState4[1];
  var fonts = useMemo(function () {
    return {
      systemFonts: uniqBy([].concat(_toConsumableArray(previousSystemFonts || []), _toConsumableArray(systemFonts || [])), function (font) {
        return isObject(font) ? font.name : font;
      }),
      googleFonts: uniqBy([].concat(_toConsumableArray(previousGoogleFonts || []), _toConsumableArray(googleFonts || [])), function (font) {
        return isObject(font) ? font.name : font;
      }),
      customFonts: uniqBy([].concat(_toConsumableArray(previousCustomFonts || []), _toConsumableArray(customFonts || [])), function (font) {
        return isObject(font) ? font.name : font;
      }),
      setGoogleFonts: setGoogleFonts
    };
  }, [previousSystemFonts, previousGoogleFonts, previousCustomFonts, customFonts, systemFonts, googleFonts, setGoogleFonts]);
  return /*#__PURE__*/React.createElement(FontsContext.Provider, {
    value: fonts
  }, children);
};
FontsProvider.propTypes = propTypes$c;
FontsProvider.defaultProps = defaultProps$c;

var GoogleApiClientContext = /*#__PURE__*/React.createContext(null);
var useGoogleApiClient = function useGoogleApiClient() {
  return useContext(GoogleApiClientContext);
};
var withGoogleApiClient = function withGoogleApiClient(WrappedComponent) {
  var getDisplayName = function getDisplayName(_ref) {
    var _ref$displayName = _ref.displayName,
      displayName = _ref$displayName === void 0 ? null : _ref$displayName,
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? null : _ref$name;
    return displayName || name || 'Component';
  };
  var WithGoogleApiClientComponent = function WithGoogleApiClientComponent(props) {
    return /*#__PURE__*/React.createElement(GoogleApiClientContext.Consumer, null, function (client) {
      return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({
        googleApiClient: client
      }, props));
    });
  };
  WithGoogleApiClientComponent.displayName = "WithGoogleApiClient(".concat(getDisplayName(WrappedComponent), ")");
  return WithGoogleApiClientComponent;
};
var propTypes$b = {
  children: PropTypes.node.isRequired
};
var defaultProps$b = {};
var GoogleApiClientProvider = function GoogleApiClientProvider(_ref2) {
  var children = _ref2.children;
  var _useGoogleKeys = useGoogleKeys(),
    apiKey = _useGoogleKeys.apiKey;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    client = _useState2[0],
    setClient = _useState2[1];
  useEffect(function () {
    loadGoogleApi().then(function (gapi) {
      return gapi.client.init({
        apiKey: apiKey
      }).then(function () {
        return gapi;
      });
    }).then(function (gapi) {
      return setClient(gapi);
    });
  }, [apiKey, setClient]);
  return /*#__PURE__*/React.createElement(GoogleApiClientContext.Provider, {
    value: client
  }, children);
};
GoogleApiClientProvider.propTypes = propTypes$b;
GoogleApiClientProvider.defaultProps = defaultProps$b;

var GoogleMapsClientContext = /*#__PURE__*/React.createContext(null);
var useGoogleMapsClient = function useGoogleMapsClient() {
  return useContext(GoogleMapsClientContext);
};
var withGoogleMapsClient = function withGoogleMapsClient(WrappedComponent) {
  var getDisplayName = function getDisplayName(_ref) {
    var _ref$displayName = _ref.displayName,
      displayName = _ref$displayName === void 0 ? null : _ref$displayName,
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? null : _ref$name;
    return displayName || name || 'Component';
  };
  var WithGoogleMapsClientComponent = function WithGoogleMapsClientComponent(props) {
    return /*#__PURE__*/React.createElement(GoogleMapsClientContext.Consumer, null, function (client) {
      return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({
        googleApiClient: client
      }, props));
    });
  };
  WithGoogleMapsClientComponent.displayName = "WithGoogleMapsClient(".concat(getDisplayName(WrappedComponent), ")");
  return WithGoogleMapsClientComponent;
};
var propTypes$a = {
  children: PropTypes.node.isRequired,
  locale: PropTypes.string,
  libraries: PropTypes.arrayOf(PropTypes.string)
};
var defaultProps$a = {
  locale: 'fr',
  libraries: null
};
var GoogleMapsClientProvider = function GoogleMapsClientProvider(_ref2) {
  var children = _ref2.children,
    locale = _ref2.locale,
    libraries = _ref2.libraries;
  var _useGoogleKeys = useGoogleKeys(),
    apiKey = _useGoogleKeys.apiKey;
  var exisitingClient = useGoogleMapsClient();
  var _useState = useState(exisitingClient),
    _useState2 = _slicedToArray(_useState, 2),
    client = _useState2[0],
    setClient = _useState2[1];
  useEffect(function () {
    if (exisitingClient === null) {
      loadGoogleMaps({
        apiKey: apiKey,
        locale: locale,
        libraries: libraries
      }).then(function (newClient) {
        setClient(newClient);
      });
    }
  }, [apiKey, locale, libraries, setClient, exisitingClient]);
  return /*#__PURE__*/React.createElement(GoogleMapsClientContext.Provider, {
    value: client
  }, children);
};
GoogleMapsClientProvider.propTypes = propTypes$a;
GoogleMapsClientProvider.defaultProps = defaultProps$a;

var ModalsContext = /*#__PURE__*/React.createContext({
  container: null
});
var useModals = function useModals() {
  return useContext(ModalsContext) || {};
};
var withModals = function withModals(WrappedComponent) {
  var WithModalsComponent = function WithModalsComponent(props) {
    return /*#__PURE__*/React.createElement(ModalsContext.Consumer, null, function (_ref) {
      var modals = _ref.modals,
        container = _ref.container,
        setContainer = _ref.setContainer,
        register = _ref.register,
        unregister = _ref.unregister;
      return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({
        modalsContainer: container,
        setModalsContainer: setContainer,
        modals: modals,
        registerModal: register,
        unregisterModal: unregister
      }, props));
    });
  };
  WithModalsComponent.displayName = "WithModals(".concat(getDisplayName(WrappedComponent), ")");
  return WithModalsComponent;
};
var propTypes$9 = {
  children: PropTypes.node.isRequired,
  container: PropTypes.object // eslint-disable-line
};

var defaultProps$9 = {
  container: null
};
var ModalsProvider = function ModalsProvider(_ref2) {
  var children = _ref2.children,
    initialContainer = _ref2.container;
  var _useState = useState(initialContainer),
    _useState2 = _slicedToArray(_useState, 2),
    container = _useState2[0],
    setContainer = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    modals = _useState4[0],
    setModals = _useState4[1];
  var modalsRef = useRef(modals);
  var register = useCallback(function (id) {
    var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var currentModals = modalsRef.current;
    var newModals = [].concat(_toConsumableArray(currentModals), [_objectSpread({
      id: id
    }, data)]);
    setModals(newModals);
    modalsRef.current = newModals;
  }, [modals, setModals]);
  var unregister = useCallback(function (id) {
    var currentModals = modalsRef.current;
    var foundIndex = currentModals.findIndex(function (_ref3) {
      var modalId = _ref3.id;
      return modalId === id;
    });
    if (foundIndex !== -1) {
      var newModals = currentModals.filter(function (_ref4) {
        var modalId = _ref4.id;
        return modalId !== id;
      });
      setModals(newModals);
      modalsRef.current = newModals;
    }
  }, [modals, setModals]);
  var value = useMemo(function () {
    return {
      modals: modals,
      container: container,
      setContainer: setContainer,
      register: register,
      unregister: unregister
    };
  }, [modals, container, setContainer, register, unregister]);
  return /*#__PURE__*/React.createElement(ModalsContext.Provider, {
    value: value
  }, children);
};
ModalsProvider.propTypes = propTypes$9;
ModalsProvider.defaultProps = defaultProps$9;

var PanelsContext = /*#__PURE__*/React.createContext({
  container: null
});
var usePanels = function usePanels() {
  return useContext(PanelsContext) || {};
};
var withPanels = function withPanels(WrappedComponent) {
  var WithPanelsComponent = function WithPanelsComponent(props) {
    return /*#__PURE__*/React.createElement(PanelsContext.Consumer, null, function (_ref) {
      var panels = _ref.panels,
        setContainer = _ref.setContainer,
        container = _ref.container,
        register = _ref.register,
        unregister = _ref.unregister;
      return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({
        panelsContainer: container,
        setPanelsContainer: setContainer,
        panels: panels,
        registerPanel: register,
        unregisterPanel: unregister
      }, props));
    });
  };
  WithPanelsComponent.displayName = "WithPanels(".concat(getDisplayName(WrappedComponent), ")");
  return WithPanelsComponent;
};
var propTypes$8 = {
  children: PropTypes.node.isRequired,
  container: PropTypes.object // eslint-disable-line
};

var defaultProps$8 = {
  container: null
};
var PanelsProvider = function PanelsProvider(_ref2) {
  var children = _ref2.children,
    initialContainer = _ref2.container;
  var _useState = useState(initialContainer),
    _useState2 = _slicedToArray(_useState, 2),
    container = _useState2[0],
    setContainer = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    panels = _useState4[0],
    setPanels = _useState4[1];
  var panelsRef = useRef(panels);
  var register = useCallback(function (id) {
    var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var currentPanels = panelsRef.current;
    var newPanels = [].concat(_toConsumableArray(currentPanels), [_objectSpread({
      id: id
    }, data)]);
    setPanels(newPanels);
    panelsRef.current = newPanels;
  }, [panels, setPanels]);
  var unregister = useCallback(function (id) {
    var currentPanels = panelsRef.current;
    var foundIndex = currentPanels.findIndex(function (_ref3) {
      var modalId = _ref3.id;
      return modalId === id;
    });
    if (foundIndex !== -1) {
      var newPanels = currentPanels.filter(function (_ref4) {
        var modalId = _ref4.id;
        return modalId !== id;
      });
      setPanels(newPanels);
      panelsRef.current = newPanels;
    }
  }, [panels, setPanels]);
  var value = useMemo(function () {
    return {
      panels: panels,
      setContainer: setContainer,
      container: container,
      register: register,
      unregister: unregister
    };
  }, [panels, setContainer, container, register, unregister]);
  return /*#__PURE__*/React.createElement(PanelsContext.Provider, {
    value: value
  }, children);
};
PanelsProvider.propTypes = propTypes$8;
PanelsProvider.defaultProps = defaultProps$8;

var defaultControlsThemeValue = {
  seekBarOnly: false,
  color: null,
  progressColor: null
};
var defaultValue$1 = {
  playing: false,
  paused: false,
  muted: true,
  controls: false,
  controlsVisible: false,
  media: null,
  controlsTheme: defaultControlsThemeValue
};
var PlaybackContext = /*#__PURE__*/React.createContext(_objectSpread(_objectSpread({}, defaultValue$1), {}, {
  setMuted: function setMuted() {},
  setPlaying: function setPlaying() {},
  setControls: function setControls() {},
  setControlsVisible: function setControlsVisible() {},
  setControlsTheme: function setControlsTheme() {},
  showControls: function showControls() {},
  hideControls: function hideControls() {},
  setMedia: function setMedia() {}
}));
var usePlaybackContext = function usePlaybackContext() {
  return useContext(PlaybackContext);
};
var usePlaybackMediaRef = function usePlaybackMediaRef(active) {
  var _usePlaybackContext = usePlaybackContext(),
    setMedia = _usePlaybackContext.setMedia;
  var mediaRef = useRef(null);
  useEffect(function () {
    if (!active) {
      return function () {};
    }
    if (mediaRef.current !== null) {
      setMedia(mediaRef.current);
    }
    return function () {
      setMedia(null);
    };
  }, [setMedia, active]);
  return mediaRef;
};
var propTypes$7 = {
  children: PropTypes.node.isRequired,
  controls: PropTypes.bool,
  controlsVisible: PropTypes.bool,
  controlsTheme: PropTypes.shape({
    seekBarOnly: PropTypes.bool,
    color: PropTypes.any,
    progressColor: PropTypes.any
  }),
  muted: PropTypes.bool,
  playing: PropTypes.bool,
  paused: PropTypes.bool
};
var defaultProps$7 = _objectSpread({}, defaultValue$1);
var PlaybackProvider = function PlaybackProvider(_ref) {
  var initialMuted = _ref.muted,
    initialPlaying = _ref.playing,
    paused = _ref.paused,
    initialControls = _ref.controls,
    initialControlsVisible = _ref.controlsVisible,
    initialControlsTheme = _ref.controlsTheme,
    children = _ref.children;
  var _useState = useState(initialMuted),
    _useState2 = _slicedToArray(_useState, 2),
    muted = _useState2[0],
    setMuted = _useState2[1];
  var _useState3 = useState(initialPlaying),
    _useState4 = _slicedToArray(_useState3, 2),
    playing = _useState4[0],
    setPlaying = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    media = _useState6[0],
    setMedia = _useState6[1];
  var _useState7 = useState(initialControls),
    _useState8 = _slicedToArray(_useState7, 2),
    controls = _useState8[0],
    setControls = _useState8[1];
  var _useState9 = useState(initialControlsVisible),
    _useState10 = _slicedToArray(_useState9, 2),
    controlsVisible = _useState10[0],
    setControlsVisible = _useState10[1];
  var _useState11 = useState(initialControlsTheme),
    _useState12 = _slicedToArray(_useState11, 2),
    controlsTheme = _useState12[0],
    setControlsTheme = _useState12[1];
  var finalSetControls = useCallback(function (newControls) {
    if (newControls) {
      setControls(true);
      setControlsVisible(true);
    } else {
      setControls(false);
      setControlsVisible(false);
    }
  }, [setControls, setControlsVisible]);
  var finalSetControlsTheme = useCallback(function (newTheme) {
    setControlsTheme(_objectSpread(_objectSpread({}, defaultControlsThemeValue), newTheme));
  }, [setControlsTheme]);
  var showControls = useCallback(function () {
    return setControlsVisible(true);
  }, [setControlsVisible]);
  var hideControls = useCallback(function () {
    return setControlsVisible(false);
  }, [setControlsVisible]);
  var hasAudio = useMemo(function () {
    if (media === null || media.tagName.toLowerCase() !== 'video') {
      return false;
    }
    if (media.tagName.toLowerCase() === 'audio') {
      return true;
    }
    if (typeof media.dataset.hasAudio === 'undefined') {
      return null;
    }
    return media.dataset.hasAudio === 'true' || media.dataset.hasAudio === true;
  }, [media]);
  var value = useMemo(function () {
    return {
      muted: muted,
      playing: playing && !paused,
      controls: controls,
      controlsVisible: controlsVisible,
      media: media,
      hasAudio: hasAudio,
      controlsTheme: controlsTheme,
      setMuted: setMuted,
      setPlaying: setPlaying,
      setControls: finalSetControls,
      setControlsVisible: setControlsVisible,
      setControlsTheme: finalSetControlsTheme,
      showControls: showControls,
      hideControls: hideControls,
      setMedia: setMedia
    };
  }, [muted, playing, paused, controls, controlsVisible, controlsTheme, media, hasAudio, setMuted, setPlaying, finalSetControls, finalSetControlsTheme, setControlsVisible, setControlsTheme, showControls, hideControls, setMedia]);
  return /*#__PURE__*/React.createElement(PlaybackContext.Provider, {
    value: value
  }, children);
};
PlaybackProvider.propTypes = propTypes$7;
PlaybackProvider.defaultProps = defaultProps$7;

/* eslint-disable react/jsx-props-no-spreading */
var RoutesContext = /*#__PURE__*/React.createContext(null);
var useRoutes = function useRoutes() {
  var _useContext = useContext(RoutesContext),
    routes = _useContext.routes;
  return routes;
};
var useUrlGenerator = function useUrlGenerator() {
  var _useContext2 = useContext(RoutesContext),
    routes = _useContext2.routes,
    basePath = _useContext2.basePath;
  var urlGenerator = useCallback(function (key, data) {
    var url = generatePath(routes[key], data);
    return basePath !== null ? "".concat(basePath.replace(/\/$/, ''), "/").concat(url.replace(/^\//, '')) : url;
  }, [generatePath, routes, basePath]);
  return urlGenerator;
};
var useRoutePush = function useRoutePush() {
  var url = useUrlGenerator();
  var history = useHistory() || null;
  var push = useCallback(function (route, data) {
    if (history !== null && history.push) {
      for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
        args[_key - 2] = arguments[_key];
      }
      if (isString(route)) {
        history.push.apply(history, [url(route, data)].concat(args));
      } else {
        var _ref = route || {},
          _ref$pathname = _ref.pathname,
          pathname = _ref$pathname === void 0 ? null : _ref$pathname,
          _ref$search = _ref.search,
          search = _ref$search === void 0 ? null : _ref$search;
        history.push.apply(history, [{
          pathname: url(pathname, data),
          search: search
        }].concat(args));
      }
    }
  }, [history, url]);
  return push;
};
var useRouteBack = function useRouteBack() {
  var url = useUrlGenerator();
  var history = useHistory() || null;
  var back = useCallback(function () {
    if (history !== null && history.goBack) {
      history.goBack();
    }
  }, [history, url]);
  return back;
};
var propTypes$6 = {
  children: PropTypes.node.isRequired,
  routes: PropTypes.objectOf(PropTypes.string),
  // .isRequired,
  basePath: PropTypes.string
};
var defaultProps$6 = {
  routes: null,
  basePath: null
};
var RoutesProvider = function RoutesProvider(_ref2) {
  var routes = _ref2.routes,
    basePath = _ref2.basePath,
    children = _ref2.children;
  var value = useMemo(function () {
    return {
      routes: routes,
      basePath: basePath
    };
  }, []);
  return /*#__PURE__*/React.createElement(RoutesContext.Provider, {
    value: value
  }, children);
};
RoutesProvider.propTypes = propTypes$6;
RoutesProvider.defaultProps = defaultProps$6;

var ScreenContext = /*#__PURE__*/React.createContext({
  data: null,
  definition: null,
  renderContext: null
});
var useScreen = function useScreen() {
  return useContext(ScreenContext);
};
var useScreenDefinition = function useScreenDefinition() {
  var intl = useIntl();
  var _ref = useScreen() || {},
    _ref$definition = _ref.definition,
    definition = _ref$definition === void 0 ? null : _ref$definition;
  var _ref2 = definition || {},
    _ref2$fields = _ref2.fields,
    fields = _ref2$fields === void 0 ? null : _ref2$fields;
  var finalFields = useMemo(function () {
    return fields !== null ? [].concat(_toConsumableArray(fields), [getScreenExtraField(intl)]) : null;
  }, [fields]);
  return _objectSpread(_objectSpread({}, definition), {}, {
    fields: finalFields
  });
};
var useScreenData = function useScreenData() {
  var _ref3 = useScreen() || {},
    data = _ref3.data;
  return data;
};
var useScreenRenderContext = function useScreenRenderContext() {
  var _ref4 = useScreen() || {},
    _ref4$renderContext = _ref4.renderContext,
    renderContext = _ref4$renderContext === void 0 ? 'view' : _ref4$renderContext;
  var isPlaceholder = renderContext === 'placeholder';
  var isPreview = renderContext === 'preview';
  var isEdit = renderContext === 'edit';
  var isView = renderContext === 'view';
  var isStatic = renderContext === 'static';
  var isCapture = renderContext === 'capture';
  return {
    renderContext: renderContext,
    isPlaceholder: isPlaceholder,
    isPreview: isPreview,
    isEdit: isEdit,
    isView: isView || isStatic || isCapture,
    isStatic: isStatic,
    isCapture: isCapture
  };
};
var useScreenState = function useScreenState() {
  var _ref5 = useScreen() || {},
    screenState = _ref5.screenState;
  return screenState;
};
var propTypes$5 = {
  children: PropTypes.node.isRequired,
  data: PropTypes$1.screen,
  definition: PropTypes$1.screenDefinition,
  renderContext: PropTypes$1.renderContext,
  screenState: PropTypes.string
};
var defaultProps$5 = {
  data: null,
  definition: null,
  renderContext: null,
  screenState: null
};
var ScreenProvider = function ScreenProvider(_ref6) {
  var data = _ref6.data,
    definition = _ref6.definition,
    renderContext = _ref6.renderContext,
    screenState = _ref6.screenState,
    children = _ref6.children;
  var _ref7 = useScreen() || {},
    _ref7$data = _ref7.data,
    previousData = _ref7$data === void 0 ? null : _ref7$data,
    _ref7$definition = _ref7.definition,
    previousDefinition = _ref7$definition === void 0 ? null : _ref7$definition,
    _ref7$renderContext = _ref7.renderContext,
    previousContext = _ref7$renderContext === void 0 ? null : _ref7$renderContext,
    _ref7$screenState = _ref7.screenState,
    previousScreenState = _ref7$screenState === void 0 ? null : _ref7$screenState;
  var finalData = data || previousData || null;
  var _ref8 = finalData || {},
    _ref8$type = _ref8.type,
    type = _ref8$type === void 0 ? null : _ref8$type;
  var screensManager = useScreensManager();
  var contextDefinition = screensManager !== null ? screensManager.getDefinition(type) : null;
  var finalDefinition = definition || contextDefinition || previousDefinition;
  var finalRenderContext = renderContext || previousContext || 'view';
  var finalScreenState = screenState || previousScreenState || null;
  var value = useMemo(function () {
    return {
      data: finalData,
      definition: finalDefinition,
      renderContext: finalRenderContext,
      screenState: finalScreenState
    };
  }, [finalData, finalDefinition, finalRenderContext, finalScreenState]);
  return /*#__PURE__*/React.createElement(ScreenContext.Provider, {
    value: value
  }, children);
};
ScreenProvider.propTypes = propTypes$5;
ScreenProvider.defaultProps = defaultProps$5;

/* eslint-disable react/jsx-props-no-spreading */
var ScreenSizeContext = /*#__PURE__*/React.createContext({
  screen: null,
  screens: [],
  width: 0,
  height: 0,
  resolution: 1,
  landscape: false
});
var useScreenSize = function useScreenSize() {
  return useContext(ScreenSizeContext);
};
var propTypes$4 = {
  children: PropTypes.node.isRequired,
  size: PropTypes$1.screenSize
};
var defaultProps$4 = {
  size: {}
};

// Note: this is done to avoid excessive renders on the screens that use the context

var ScreenSizeProvider = function ScreenSizeProvider(_ref) {
  var size = _ref.size,
    children = _ref.children;
  var nextScreen = size.screen,
    nextWidth = size.width,
    nextHeight = size.height,
    nextResolution = size.resolution;
  var currentSize = useMemo(function () {
    return size;
  }, [nextScreen, nextWidth, nextHeight, nextResolution]);
  return /*#__PURE__*/React.createElement(ScreenSizeContext.Provider, {
    value: currentSize
  }, children);
};
ScreenSizeProvider.propTypes = propTypes$4;
ScreenSizeProvider.defaultProps = defaultProps$4;

var useTracking = function useTracking() {
  return useContext(TrackingContext);
};
var propTypes$3 = {
  children: PropTypes.node.isRequired,
  variables: PropTypes$1.trackingVariables
};
var defaultProps$3 = {
  variables: null
};
var TrackingProvider = function TrackingProvider(_ref) {
  var variables = _ref.variables,
    children = _ref.children;
  var contextTracking = useTracking() || null;
  var tracking = useMemo(function () {
    return new Tracking({
      variables: _objectSpread(_objectSpread({}, contextTracking !== null ? contextTracking.getVariables() : null), variables)
    });
  }, [contextTracking, variables]);
  return /*#__PURE__*/React.createElement(TrackingContainer, {
    tracking: tracking
  }, children);
};
TrackingProvider.propTypes = propTypes$3;
TrackingProvider.defaultProps = defaultProps$3;

var _excluded$3 = ["Uppy"];

/**
 * Locale loader
 */
var packageCache = null;
var useUppyCore = function useUppyCore() {
  // transport
  var _useState = useState({
      "package": packageCache
    }),
    _useState2 = _slicedToArray(_useState, 2),
    _useState2$0$package = _useState2[0]["package"],
    loadedPackage = _useState2$0$package === void 0 ? null : _useState2$0$package,
    setLoadedPackage = _useState2[1];
  useEffect(function () {
    var canceled = false;
    if (loadedPackage !== null) {
      return function () {
        canceled = true;
      };
    }
    import('@uppy/core').then(function (_ref) {
      var Uppy = _ref.Uppy,
        other = _objectWithoutProperties(_ref, _excluded$3);
      var _ref2 = other || {},
        defaultPackage = _ref2["default"];
      packageCache = Uppy || defaultPackage;
      if (!canceled) {
        setLoadedPackage({
          "package": Uppy || defaultPackage
        });
      }
    });
    return function () {
      canceled = true;
    };
  }, [loadedPackage, setLoadedPackage]);
  return loadedPackage;
};

/**
 * Locale loader
 */
var packagesCache$2 = {};
var defaultPackagesMap$2 = {
  fr: function fr() {
    return import('@uppy/locales/lib/fr_FR');
  },
  en: function en() {
    return import('@uppy/locales/lib/en_US');
  }
};
var useUppyLocale = function useUppyLocale(locale) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$packagesMap = _ref.packagesMap,
    packagesMap = _ref$packagesMap === void 0 ? defaultPackagesMap$2 : _ref$packagesMap;
  var _useState = useState({
      "package": packagesCache$2[locale] || null
    }),
    _useState2 = _slicedToArray(_useState, 2),
    _useState2$0$package = _useState2[0]["package"],
    loadedPackage = _useState2$0$package === void 0 ? null : _useState2$0$package,
    setLoadedPackage = _useState2[1];
  var packageLoader = packagesMap[locale] || null;
  useEffect(function () {
    var canceled = false;
    if (loadedPackage !== null || packageLoader === null) {
      return function () {
        canceled = true;
      };
    }
    packageLoader().then(function (_ref2) {
      var dep = _ref2["default"];
      // packagesCache[locale] = dep;
      if (!canceled) {
        setLoadedPackage({
          "package": dep
        });
      }
    });
    return function () {
      canceled = true;
    };
  }, [packageLoader, loadedPackage, setLoadedPackage]);
  return loadedPackage;
};

var _excluded$2 = ["default"];

/**
 * Locale loader
 */
var packagesCache$1 = {};
var defaultPackagesMap$1 = {
  webcam: function webcam() {
    return import('@uppy/webcam');
  },
  facebook: function facebook() {
    return import('@uppy/facebook');
  },
  instagram: function instagram() {
    return import('@uppy/instagram');
  },
  dropbox: function dropbox() {
    return import('@uppy/dropbox');
  },
  'google-drive': function googleDrive() {
    return import('@uppy/google-drive');
  }
};
var useUppySources = function useUppySources(sources) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$packagesMap = _ref.packagesMap,
    packagesMap = _ref$packagesMap === void 0 ? defaultPackagesMap$1 : _ref$packagesMap;
  // transport
  var _useState = useState({
      packages: sources.reduce(function (map, source) {
        var sourcePackage = packagesCache$1[source] || null;
        if (sourcePackage === null) {
          return map;
        }
        return _objectSpread(_objectSpread({}, map), {}, _defineProperty({}, source, sourcePackage));
      }, null)
    }),
    _useState2 = _slicedToArray(_useState, 2),
    _useState2$0$packages = _useState2[0].packages,
    loadedPackages = _useState2$0$packages === void 0 ? [] : _useState2$0$packages,
    setLoadedPackages = _useState2[1];
  var sourcesToLoad = useMemo(function () {
    if (loadedPackages === null) {
      return sources;
    }
    var sourcesLoaded = Object.keys(loadedPackages);
    return sources.filter(function (source) {
      return sourcesLoaded.indexOf(source) === -1;
    });
  }, [sources, loadedPackages]);
  useEffect(function () {
    var canceled = false;
    if (sourcesToLoad.length === 0) {
      return function () {
        canceled = true;
      };
    }
    Promise.all(sourcesToLoad.map(function (source) {
      return packagesMap[source] || null;
    }).filter(function (it) {
      return it !== null;
    }).map(function (promise) {
      return promise();
    })).then(function (packagesLoaded) {
      var newLoadedPackages = sourcesToLoad.reduce(function (map, source, index) {
        var _packagesLoaded$index = packagesLoaded[index],
          pack = _packagesLoaded$index["default"],
          others = _objectWithoutProperties(_packagesLoaded$index, _excluded$2);
        return _objectSpread(_objectSpread({}, map), {}, _defineProperty({}, source, Object.keys(others).reduce(function (otherMap, key) {
          otherMap[key] = others[key]; // eslint-disable-line no-param-reassign
          return otherMap;
        }, pack)));
      }, {});
      packagesCache$1 = _objectSpread(_objectSpread({}, packagesCache$1), newLoadedPackages);
      if (!canceled) {
        setLoadedPackages({
          packages: newLoadedPackages
        });
      }
    });
    return function () {
      canceled = true;
    };
  }, [sourcesToLoad, packagesMap, loadedPackages, setLoadedPackages]);
  return sourcesToLoad.length === 0 ? loadedPackages : null;
};

var _excluded$1 = ["default"];

/**
 * Locale loader
 */
var packagesCache = {};
var defaultPackagesMap = {
  transloadit: null,
  // Disabled for compatibility reasons
  tus: function tus() {
    return import('@uppy/tus');
  },
  xhr: function xhr() {
    return import('@uppy/xhr-upload');
  }
};
var useUppyTransport = function useUppyTransport(transport) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$packagesMap = _ref.packagesMap,
    packagesMap = _ref$packagesMap === void 0 ? defaultPackagesMap : _ref$packagesMap;
  // transport
  var _useState = useState({
      "package": packagesCache[transport] || null
    }),
    _useState2 = _slicedToArray(_useState, 2),
    _useState2$0$package = _useState2[0]["package"],
    loadedPackage = _useState2$0$package === void 0 ? null : _useState2$0$package,
    setLoadedPackage = _useState2[1];
  var packageLoader = packagesMap[transport] || null;
  useEffect(function () {
    var canceled = false;
    if (loadedPackage !== null || packageLoader === null) {
      return function () {
        canceled = true;
      };
    }
    packageLoader().then(function (_ref2) {
      var pack = _ref2["default"],
        others = _objectWithoutProperties(_ref2, _excluded$1);
      var dep = Object.keys(others).reduce(function (map, key) {
        map[key] = others[key]; // eslint-disable-line no-param-reassign
        return map;
      }, pack);
      packagesCache[transport] = dep;
      if (!canceled) {
        setLoadedPackage({
          "package": dep
        });
      }
    });
    return function () {
      canceled = true;
    };
  }, [packageLoader, loadedPackage, setLoadedPackage]);
  return loadedPackage;
};

var convertToMedia = function convertToMedia(it) {
  // console.log('upload', it);
  var type = it.data.type.split('/')[0];
  var thumbnail = it.transloadit["".concat(type, "_thumbnail")] || null;
  var original = it.transloadit["".concat(type, "_original")] || null;
  return {
    handle: it.id,
    type: type,
    name: it.meta.name,
    mime: it.data.type,
    size: it.data.size,
    url: original !== null ? original.ssl_url || original.url : null,
    thumbnail_url: thumbnail !== null ? thumbnail.ssl_url || thumbnail.url : null,
    metadata: _objectSpread(_objectSpread(_objectSpread({}, original !== null ? original.meta || null : null), it.meta.user ? {
      user: it.meta.user
    } || null : null), {}, {
      filename: it.meta.filename,
      transloadit: it.transloadit.results || null
    })
  };
};
var getTransloaditMediasFromResponse = function getTransloaditMediasFromResponse(response) {
  return response.successful.map(function (it) {
    var transloadit = response.transloadit.find(function (subIt) {
      return subIt.assembly_id === it.transloadit.assembly;
    }) || null;
    var results = transloadit !== null ? transloadit.results || null : null;
    return _objectSpread(_objectSpread({}, it), {}, {
      transloadit: results !== null ? Object.keys(results).reduce(function (map, resultKey) {
        var result = results[resultKey].find(function (itResult) {
          return itResult.name === it.name;
        });
        return result !== null ? _objectSpread(_objectSpread({}, map), {}, _defineProperty({}, resultKey, result)) : map;
      }, null) : null
    });
  }).filter(function (it) {
    return it.transloadit !== null;
  }).map(function (it) {
    return convertToMedia(it);
  });
};

var _excluded = ["sources"],
  _excluded2 = ["key", "templateId", "waitForEncoding"];
var UppyContext = /*#__PURE__*/React.createContext(null);
var useUppyConfig = function useUppyConfig() {
  var _ref = useContext(UppyContext) || {},
    _ref$transport = _ref.transport,
    transport = _ref$transport === void 0 ? null : _ref$transport,
    _ref$locale = _ref.locale,
    locale = _ref$locale === void 0 ? null : _ref$locale,
    _ref$sources = _ref.sources,
    sources = _ref$sources === void 0 ? null : _ref$sources,
    _ref$transloadit = _ref.transloadit,
    transloadit = _ref$transloadit === void 0 ? null : _ref$transloadit,
    _ref$companion = _ref.companion,
    companion = _ref$companion === void 0 ? null : _ref$companion,
    _ref$tus = _ref.tus,
    tus = _ref$tus === void 0 ? null : _ref$tus,
    _ref$xhr = _ref.xhr,
    xhr = _ref$xhr === void 0 ? null : _ref$xhr;
  return {
    transport: transport,
    locale: locale,
    sources: sources,
    transloadit: transloadit,
    companion: companion,
    tus: tus,
    xhr: xhr
  };
};
var useUppy = function useUppy() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref2$onComplete = _ref2.onComplete,
    onComplete = _ref2$onComplete === void 0 ? null : _ref2$onComplete,
    _ref2$onFail = _ref2.onFail,
    onFail = _ref2$onFail === void 0 ? null : _ref2$onFail,
    _ref2$getFileName = _ref2.getFileName,
    getFileName = _ref2$getFileName === void 0 ? function (_ref3) {
      var _ref3$extension = _ref3.extension,
        extension = _ref3$extension === void 0 ? null : _ref3$extension;
      return "".concat(v1()).concat(extension !== null ? ".".concat(extension) : '');
    } : _ref2$getFileName,
    _ref2$meta = _ref2.meta,
    meta = _ref2$meta === void 0 ? null : _ref2$meta,
    _ref2$maxNumberOfFile = _ref2.maxNumberOfFiles,
    maxNumberOfFiles = _ref2$maxNumberOfFile === void 0 ? 30 : _ref2$maxNumberOfFile,
    _ref2$allowedFileType = _ref2.allowedFileTypes,
    allowedFileTypes = _ref2$allowedFileType === void 0 ? null : _ref2$allowedFileType,
    _ref2$autoProceed = _ref2.autoProceed,
    autoProceed = _ref2$autoProceed === void 0 ? false : _ref2$autoProceed;
  var _ref4 = useContext(UppyContext) || null,
    buildUppy = _ref4.buildUppy,
    transport = _ref4.transport;
  var uppy = useMemo(function () {
    return buildUppy !== null ? buildUppy({
      meta: meta,
      restrictions: {
        maxNumberOfFiles: maxNumberOfFiles,
        allowedFileTypes: allowedFileTypes
      },
      autoProceed: autoProceed
    }) : null;
  }, [buildUppy, meta, maxNumberOfFiles, allowedFileTypes, autoProceed]);
  useEffect(function () {
    if (uppy === null) {
      return function () {};
    }
    var onUppyComplete = function onUppyComplete(response) {
      var _response$successful = response.successful,
        successful = _response$successful === void 0 ? [] : _response$successful,
        _response$failed = response.failed,
        failed = _response$failed === void 0 ? null : _response$failed;
      var finalSuccessful = transport === 'transloadit' ? getTransloaditMediasFromResponse(response) : successful;
      if (onComplete !== null) {
        onComplete(finalSuccessful);
      }
      if (onFail !== null) {
        onFail(failed);
      }
    };
    uppy.on('complete', onUppyComplete);
    return function () {
      uppy.off('complete', onUppyComplete);
    };
  }, [uppy, transport, onComplete]);
  useEffect(function () {
    if (uppy === null) {
      return function () {};
    }
    var onUpload = function onUpload(_ref5) {
      var _ref5$fileIDs = _ref5.fileIDs,
        ids = _ref5$fileIDs === void 0 ? [] : _ref5$fileIDs;
      ids.forEach(function (id) {
        var file = uppy.getFile(id);
        var newName = getFileName(file);
        if (newName !== null) {
          uppy.setFileMeta(id, {
            name: newName
          });
        }
      });
    };
    uppy.on('upload', onUpload);
    return function () {
      uppy.off('upload', onUpload);
    };
  }, [uppy]);
  useEffect(function () {
    return function () {
      if (uppy !== null) {
        uppy.close();
      }
    };
  }, [uppy]);
  return uppy;
};
var propTypes$2 = {
  children: PropTypes.node.isRequired,
  transport: PropTypes.oneOf(['xhr', 'transloadit', 'tus']),
  locale: PropTypes.string,
  sources: PropTypes.arrayOf(PropTypes.oneOf(['webcam', 'facebook', 'instagram', 'dropbox', 'google-drive'])),
  transloadit: PropTypes.shape({
    key: PropTypes.string.isRequired,
    templateId: PropTypes.string,
    waitForEncoding: PropTypes.bool
  }),
  companion: PropTypes.shape({
    url: PropTypes.string.isRequired,
    allowedHosts: PropTypes.string.isRequired
  }),
  tus: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    endpoint: PropTypes.string.isRequired
  })]),
  xhr: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    endpoint: PropTypes.string.isRequired
  })])
};
var defaultProps$2 = {
  transport: null,
  locale: null,
  sources: null,
  transloadit: null,
  companion: null,
  tus: null,
  xhr: null
};
var UppyProvider = function UppyProvider(_ref6) {
  var children = _ref6.children,
    providedTransport = _ref6.transport,
    providedLocale = _ref6.locale,
    providedSources = _ref6.sources,
    providedTransloadit = _ref6.transloadit,
    providedCompanion = _ref6.companion,
    providedTus = _ref6.tus,
    providedXhr = _ref6.xhr;
  var _useIntl = useIntl(),
    intlLocale = _useIntl.locale;
  var _ref7 = useContext(UppyContext) || {},
    _ref7$transport = _ref7.transport,
    contextTransport = _ref7$transport === void 0 ? null : _ref7$transport,
    _ref7$locale = _ref7.locale,
    contextLocale = _ref7$locale === void 0 ? null : _ref7$locale,
    _ref7$sources = _ref7.sources,
    contextSources = _ref7$sources === void 0 ? null : _ref7$sources,
    _ref7$transloadit = _ref7.transloadit,
    contextTransloadit = _ref7$transloadit === void 0 ? null : _ref7$transloadit,
    _ref7$companion = _ref7.companion,
    contextCompanion = _ref7$companion === void 0 ? null : _ref7$companion,
    _ref7$tus = _ref7.tus,
    contextTus = _ref7$tus === void 0 ? null : _ref7$tus,
    _ref7$xhr = _ref7.xhr,
    contextXhr = _ref7$xhr === void 0 ? null : _ref7$xhr;
  var transport = providedTransport || contextTransport || 'xhr';
  var locale = providedLocale || contextLocale || intlLocale;
  var sources = providedSources || contextSources || ['webcam', 'facebook', 'instagram', 'dropbox', 'google-drive'];
  var transloadit = providedTransloadit || contextTransloadit;
  var companion = providedCompanion || contextCompanion;
  var tus = providedTus || contextTus;
  var xhr = providedXhr || contextXhr;
  var Uppy = useUppyCore();
  var uppyTransport = useUppyTransport(transport);
  var uppySources = useUppySources(sources);
  var uppyLocale = useUppyLocale(locale || intlLocale);
  var buildUppy = useMemo(function () {
    if (Uppy === null || uppyLocale === null || uppyTransport === null || uppySources === null) {
      return null;
    }
    return function () {
      var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var _ref8 = opts || {},
        _ref8$sources = _ref8.sources,
        customSources = _ref8$sources === void 0 ? sources : _ref8$sources,
        uppyOpts = _objectWithoutProperties(_ref8, _excluded);
      var newUppy = new Uppy(_objectSpread({
        locale: uppyLocale
      }, uppyOpts));
      if (transport === 'transloadit') {
        var key = transloadit.key,
          templateId = transloadit.templateId,
          _transloadit$waitForE = transloadit.waitForEncoding,
          waitForEncoding = _transloadit$waitForE === void 0 ? true : _transloadit$waitForE,
          transloaditOpts = _objectWithoutProperties(transloadit, _excluded2);
        newUppy.use(uppyTransport, {
          params: _objectSpread({
            auth: {
              key: key
            },
            template_id: templateId
          }, transloaditOpts),
          waitForEncoding: waitForEncoding
        });
      } else if (transport === 'tus') {
        newUppy.use(uppyTransport, _objectSpread({
          endpoint: '/tus',
          resume: true,
          retryDelays: [0, 1000, 3000, 5000]
        }, tus));
      } else if (transport === 'xhr') {
        newUppy.use(uppyTransport, _objectSpread({
          endpoint: isString(xhr) ? xhr : '/upload'
        }, isObject(xhr) ? xhr : null));
      }
      if (transport === 'transloadit' || companion !== null) {
        return customSources.reduce(function (currentUppy, sourceId) {
          var source = uppySources[sourceId] || null;
          if (source === null) {
            return currentUppy;
          }
          var _ref9 = companion || {
              url: uppyTransport.COMPANION || null,
              allowedHosts: uppyTransport.COMPANION_PATTERN || null
            },
            companionUrl = _ref9.url,
            companionAllowedHosts = _ref9.allowedHosts;
          return newUppy.use(source, {
            id: sourceId,
            companionUrl: companionUrl,
            companionAllowedHosts: companionAllowedHosts
          });
        }, newUppy);
      }
      return newUppy;
    };
  }, [Uppy, uppyLocale, uppyTransport, uppySources, transport, sources, transloadit, companion, tus, xhr]);
  var value = useMemo(function () {
    return {
      transport: transport,
      locale: locale,
      sources: sources,
      transloadit: transloadit,
      companion: companion,
      tus: tus,
      xhr: xhr,
      Uppy: Uppy,
      uppyTransport: uppyTransport,
      uppySources: uppySources,
      uppyLocale: uppyLocale,
      buildUppy: buildUppy
    };
  }, [transport, locale, sources, transloadit, companion, tus, xhr, Uppy, uppyTransport, uppySources, uppyLocale, buildUppy]);
  return /*#__PURE__*/React.createElement(UppyContext.Provider, {
    value: value
  }, children);
};
UppyProvider.propTypes = propTypes$2;
UppyProvider.defaultProps = defaultProps$2;

var defaultValue = {
  events: new EventEmitter(),
  menuVisible: false,
  menuOverScreen: false,
  topHeight: 0,
  bottomHeight: 0,
  bottomSidesWidth: 0,
  gotoNextScreen: function gotoNextScreen() {},
  gotoPreviousScreen: function gotoPreviousScreen() {},
  disableInteraction: function disableInteraction() {},
  enableInteraction: function enableInteraction() {}
};
var ViewerContext = /*#__PURE__*/React.createContext(_objectSpread(_objectSpread({}, defaultValue), {}, {
  webView: null,
  setWebView: function setWebView() {}
}));
var useViewerContext = function useViewerContext() {
  return useContext(ViewerContext);
};
var useViewerSize = function useViewerSize() {
  var _useViewerContext = useViewerContext(),
    width = _useViewerContext.width,
    height = _useViewerContext.height;
  return {
    width: width,
    height: height
  };
};
var useViewerNavigation = function useViewerNavigation() {
  var _useViewerContext2 = useViewerContext(),
    gotoNextScreen = _useViewerContext2.gotoNextScreen,
    gotoPreviousScreen = _useViewerContext2.gotoPreviousScreen;
  return {
    gotoNextScreen: gotoNextScreen,
    gotoPreviousScreen: gotoPreviousScreen
  };
};
var useViewerEvents = function useViewerEvents() {
  var _useViewerContext3 = useViewerContext(),
    events = _useViewerContext3.events;
  return events;
};
var useViewerContainer = function useViewerContainer() {
  var _useViewerContext4 = useViewerContext(),
    _useViewerContext4$co = _useViewerContext4.containerRef,
    containerRef = _useViewerContext4$co === void 0 ? null : _useViewerContext4$co;
  return containerRef !== null ? containerRef.current : null;
};
var useViewerInteraction = function useViewerInteraction() {
  var _useViewerContext5 = useViewerContext(),
    disableInteraction = _useViewerContext5.disableInteraction,
    enableInteraction = _useViewerContext5.enableInteraction;
  return {
    disableInteraction: disableInteraction,
    enableInteraction: enableInteraction
  };
};
var useViewerWebView = function useViewerWebView() {
  var _useViewerContext6 = useViewerContext(),
    webView = _useViewerContext6.webView,
    setWebView = _useViewerContext6.setWebView;
  var value = useMemo(function () {
    return _objectSpread(_objectSpread({}, webView), {}, {
      opened: webView !== null,
      open: function open(newWebView) {
        return setWebView(isString(newWebView) ? {
          url: newWebView
        } : newWebView);
      },
      close: function close() {
        return setWebView(null);
      },
      update: function update(newWebView) {
        return setWebView(_objectSpread(_objectSpread({}, webView), newWebView));
      }
    });
  }, [webView, setWebView]);
  return value;
};
var propTypes$1 = {
  children: PropTypes.node.isRequired,
  events: PropTypes.instanceOf(EventEmitter),
  containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    current: PropTypes.any
  })]),
  menuVisible: PropTypes.bool,
  menuOverScreen: PropTypes.bool,
  width: PropTypes.number,
  height: PropTypes.number,
  topHeight: PropTypes.number,
  bottomHeight: PropTypes.number,
  bottomSidesWidth: PropTypes.number,
  gotoNextScreen: PropTypes.func,
  gotoPreviousScreen: PropTypes.func,
  disableInteraction: PropTypes.func,
  enableInteraction: PropTypes.func
};
var defaultProps$1 = _objectSpread({}, defaultValue);
var ViewerProvider = function ViewerProvider(_ref) {
  var children = _ref.children,
    containerRef = _ref.containerRef,
    events = _ref.events,
    menuVisible = _ref.menuVisible,
    menuOverScreen = _ref.menuOverScreen,
    width = _ref.width,
    height = _ref.height,
    topHeight = _ref.topHeight,
    bottomHeight = _ref.bottomHeight,
    bottomSidesWidth = _ref.bottomSidesWidth,
    gotoNextScreen = _ref.gotoNextScreen,
    gotoPreviousScreen = _ref.gotoPreviousScreen,
    disableInteraction = _ref.disableInteraction,
    enableInteraction = _ref.enableInteraction;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    webView = _useState2[0],
    setWebView = _useState2[1];
  var value = useMemo(function () {
    return {
      containerRef: containerRef,
      events: events,
      menuVisible: menuVisible,
      menuOverScreen: menuOverScreen,
      width: width,
      height: height,
      topHeight: topHeight,
      bottomHeight: bottomHeight,
      bottomSidesWidth: bottomSidesWidth,
      gotoNextScreen: gotoNextScreen,
      gotoPreviousScreen: gotoPreviousScreen,
      disableInteraction: disableInteraction,
      enableInteraction: enableInteraction,
      webView: webView,
      setWebView: setWebView
    };
  }, [containerRef, events, menuVisible, menuOverScreen, width, height, topHeight, bottomHeight, bottomSidesWidth, gotoNextScreen, gotoPreviousScreen, disableInteraction, enableInteraction, webView, setWebView]);
  return /*#__PURE__*/React.createElement(ViewerContext.Provider, {
    value: value
  }, children);
};
ViewerProvider.propTypes = propTypes$1;
ViewerProvider.defaultProps = defaultProps$1;

var VisitorContext = /*#__PURE__*/React.createContext({
  visitor: null,
  setVisitor: function setVisitor() {}
});
var useVisitorContext = function useVisitorContext() {
  var _useContext = useContext(VisitorContext),
    visitor = _useContext.visitor,
    setVisitor = _useContext.setVisitor;
  return {
    visitor: visitor,
    setVisitor: setVisitor
  };
};
var useVisitor = function useVisitor() {
  var _useContext2 = useContext(VisitorContext),
    visitor = _useContext2.visitor;
  return visitor;
};
var useSetVisitor = function useSetVisitor() {
  var _useContext3 = useContext(VisitorContext),
    setVisitor = _useContext3.setVisitor;
  return setVisitor;
};
var propTypes = {
  children: PropTypes.node.isRequired,
  visitor: PropTypes$1.visitor
};
var defaultProps = {
  visitor: null
};
var VisitorProvider = function VisitorProvider(_ref) {
  var providedVisitor = _ref.visitor,
    children = _ref.children;
  var _useState = useState(providedVisitor),
    _useState2 = _slicedToArray(_useState, 2),
    visitor = _useState2[0],
    setVisitor = _useState2[1];
  useEffect(function () {
    if (providedVisitor !== visitor) {
      setVisitor(providedVisitor);
    }
  }, [providedVisitor, setVisitor]);
  var value = useMemo(function () {
    return {
      visitor: visitor,
      setVisitor: function setVisitor(newVisitor) {
        return isString$1(newVisitor) ? {
          id: newVisitor
        } : newVisitor;
      }
    };
  }, [visitor, setVisitor]);
  return /*#__PURE__*/React.createElement(VisitorContext.Provider, {
    value: value
  }, children);
};
VisitorProvider.propTypes = propTypes;
VisitorProvider.defaultProps = defaultProps;

export { ComponentsContext, ComponentsProvider, ELEMENTS_NAMESPACE, EditorContext, EditorProvider, FIELDS_NAMESPACE, FORMS_NAMESPACE, FieldContext, FieldContextProvider, FieldsContext, FieldsProvider, FieldsValueContext, FieldsValueContextProvider, FontsContext, FontsProvider, GoogleApiClientContext, GoogleApiClientProvider, GoogleKeysContext, GoogleKeysProvider, GoogleMapsClientContext, GoogleMapsClientProvider, MODALS_NAMESPACE, ModalsContext, ModalsProvider, PanelsContext, PanelsProvider, PlaybackContext, PlaybackProvider, RoutesContext, RoutesProvider, SCREENS_NAMESPACE, ScreenContext, ScreenProvider, ScreenSizeContext, ScreenSizeProvider, ScreensContext, ScreensProvider, StoryContext, StoryProvider, TrackingProvider, UppyContext, UppyProvider, ViewerContext, ViewerProvider, VisitorContext, VisitorProvider, useComponent, useComponents, useComponentsManager, useEditor, useElementComponent, useElementsComponents, useElementsComponentsManager, useFieldComponent, useFieldContext, useFieldDefinition, useFieldsComponents, useFieldsComponentsManager, useFieldsManager, useFieldsValue, useFonts, useFormComponent, useFormsComponents, useFormsComponentsManager, useGetColors, useGoogleApiClient, useGoogleFonts, useGoogleKeys, useGoogleMapsClient, useModalComponent, useModals, useModalsComponents, useModalsComponentsManager, usePanels, usePlaybackContext, usePlaybackMediaRef, useRouteBack, useRoutePush, useRoutes, useScreen, useScreenComponent, useScreenData, useScreenDefinition, useScreenRenderContext, useScreenSize, useScreenState, useScreensComponents, useScreensComponentsManager, useScreensManager, useSetVisitor, useStory, useStoryContext, useTracking, useUppy, useUppyConfig, useUrlGenerator, useViewerContainer, useViewerContext, useViewerEvents, useViewerInteraction, useViewerNavigation, useViewerSize, useViewerWebView, useVisitor, useVisitorContext, withGoogleApiClient, withGoogleMapsClient, withModals, withPanels };
